import { useMemo } from "react";
import {
  Product,
  useProductAttributeGetter,
} from "@made-people/grease-product";
import { z } from "zod";
import { siblingsColorSwatchBlueprint } from "@made-people/blueprint-product/blueprint";
import { useTranslate } from "@made-people/grease-translations";

// Taken from src/frontend/blueprint/product/src/components/siblings/templates/color-swatch/SiblingColorSwatchList.tsx
// Changes are that we have a button fir changing the product data between variants and not a link to PDP.

const COLOR_SWATCH_ATTRIBUTE_KEY = "product_color_image_url";

const isHexColor = (value: string): boolean =>
  /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(value);

const isNamedColor = (value: string): boolean =>
  /^[a-zA-Z]+$/.test(value) || /^[a-zA-Z]+\s[a-zA-Z]+$/.test(value);

const isRgbColor = (value: string): boolean =>
  /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(value) ||
  /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/.test(value);

const isImageUrl = (value: string): boolean =>
  value.startsWith("http") ||
  value.startsWith("/") ||
  /\.(png|jpe?g|gif|svg|webp)$/i.test(value);

export const QuickShopColorPicker = ({
  allProducts,
  selectedProductId,
  onColorChange,
}: {
  allProducts: Product[];
  selectedProductId: string;
  onColorChange: (productId: string) => void;
}) => {
  const slots = siblingsColorSwatchBlueprint.styles();
  const translate = useTranslate();

  const sortedProducts = useMemo(
    () =>
      [...allProducts].sort((a, b) =>
        a.base.productId.localeCompare(b.base.productId),
      ),
    [allProducts],
  );

  const selectedProduct =
    allProducts.find((p) => p.base.productId === selectedProductId) ||
    allProducts[0];

  const selectedProductAttributeGetter = useProductAttributeGetter(
    selectedProduct.base,
  );

  if (allProducts.length <= 1) {
    return null;
  }

  const selectedColorAttribute = selectedProductAttributeGetter(
    COLOR_SWATCH_ATTRIBUTE_KEY,
    z.string(),
  );

  return (
    <div className={slots.base}>
      <div className={slots.header}>
        {translate("PDP_Product_ColorSwatch_Label", {
          color: selectedColorAttribute?.valueLabel || "",
        })}
      </div>
      <div className={slots.siblingsWrapper}>
        {sortedProducts.map((product) => {
          return (
            <ColorSwatch
              key={product.base.productId}
              product={product}
              isSelected={product.base.productId === selectedProductId}
              onColorChange={onColorChange}
              className={slots.sibling}
            />
          );
        })}
      </div>
    </div>
  );
};

const ColorSwatch = ({
  product,
  isSelected,
  onColorChange,
  className,
}: {
  product: Product;
  isSelected: boolean;
  onColorChange: (productId: string) => void;
  className?: string;
}) => {
  const attributeGetter = useProductAttributeGetter(product.base);

  const colorSwatchAttribute = attributeGetter(
    COLOR_SWATCH_ATTRIBUTE_KEY,
    z.string(),
  );

  if (!colorSwatchAttribute?.valueLabel) return null;

  const colorSwatchAttributeLabel = colorSwatchAttribute.valueLabel;
  const colorSwatchAttributeValue = colorSwatchAttribute.value;

  const getBackgroundStyle = () => {
    if (!colorSwatchAttributeValue) return {};

    if (isImageUrl(colorSwatchAttributeValue)) {
      return {
        backgroundImage: `url(${colorSwatchAttributeValue})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    }

    if (
      isHexColor(colorSwatchAttributeValue) ||
      isNamedColor(colorSwatchAttributeValue) ||
      isRgbColor(colorSwatchAttributeValue)
    ) {
      return { backgroundColor: colorSwatchAttributeValue };
    }

    return { backgroundColor: "#cccccc" };
  };

  const ariaLabel = `${colorSwatchAttributeLabel || ""} color option`;

  return (
    <button
      onClick={() => onColorChange(product.base.productId)}
      className={className}
      style={getBackgroundStyle()}
      data-is-selected={isSelected}
      data-is-white={
        colorSwatchAttributeValue === "#ffffff" ||
        colorSwatchAttributeLabel?.toLocaleLowerCase() === "white" ||
        colorSwatchAttributeLabel?.toLocaleLowerCase() === "vit"
      }
      aria-label={ariaLabel}
      title={colorSwatchAttributeLabel || ""}
      data-testid={`color-swatch-${colorSwatchAttributeLabel
        ?.toLowerCase()
        .replace(/\s+/g, "-")}`}
    />
  );
};
